import React, { useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { colors, tokens } from "../../utils";
import TextView from "../TextView";
import { ASSETS } from "../../utils/config";
import { device } from "../../utils/device";
import SearchIcon from "@mui/icons-material/Search";
import { Close } from "@mui/icons-material";
import { passwordToggleLabel, passwordInputType } from "./passwordToggle";

const InputWrapper = styled.div`
  margin-bottom: 24px;
  position: relative;

  @media (${device.mobilePortrait}) {
    margin-bottom: 16px;
  }
`;

// BUG-auth-onboarding-4cf2f4: a no-op keyframe used purely as an autofill
// detector — see the `&:-webkit-autofill` block below. Exported so consumers
// (Login) and tests can match `event.animationName` against its generated name.
export const autofillPing = keyframes`
  from { opacity: 1; }
  to { opacity: 1; }
`;

const secondaryStyles = css`
  background-color: white;
  border: 1px solid ${colors.lightBorder};
`;

const successStyles = css`
  border-color: #29c18c;
`;

const errorStyles = css`
  border-color: ${colors.error};
`;

const CustomInput = styled.input`
  width: 100%;
  background-color: ${({ customBgColor }) =>
    customBgColor || colors.background};
  border-radius: 4px;
  height: 36px;
  outline: none;
  border: ${({ customBgColor }) => "1px solid #CED4DA" || "none"};
  padding: 5px 10px;
  margin-top: 5px;
  font-size: 12px;
  position: relative;

  @media (${device.mobilePortrait}) {
    font-size: 16px;
  }

  ${({ variant }) => variant === "secondary" && secondaryStyles}

  ${({ isValid }) => isValid === true && successStyles}
  ${({ isValid }) => isValid === false && errorStyles}
  /* forceInvalid: show the error border even when the field is empty
     (required-field state) — the deck's elevated validation design. Placed
     last so it wins over variant/isValid borders. */
  ${({ forceInvalid }) => forceInvalid && errorStyles}

  /* ISP-9999 (gordon-0923-P1-REPEAT-kelly-field-focus-style-one-shared-
     border-only-app-wide) — supersedes the #511 box-shadow ring above.
     Gordon's rule for DATA FIELDS is stricter: border-color recolor only,
     no outline, no box-shadow — matching the Edit Wheel "Wheel Name" Froala
     reference field (src/theme/tokens.js "dataFieldFocus"). This field
     always renders a real 1px resting border above, so the swap is a pure
     colour change with no layout jump. */
  &:focus-visible {
    box-shadow: none;
    border-color: ${tokens.dataFieldFocus.color};
  }

  &:disabled {
    background-color: ${colors.disabled};
    cursor: not-allowed;
  }

  &:-webkit-autofill {
    -webkit-box-shadow: ${({ variant }) =>
      `0 0 0 30px ${
        variant === "secondary" ? colors.white : colors.background
      } inset !important`};
    -webkit-text-fill-color: ${colors.text};
    /* BUG-auth-onboarding-4cf2f4: Chromium autofill writes the DOM value
       WITHOUT any React-visible event until the first user gesture, so a
       Formik-gated submit button stays disabled with a visibly filled form.
       This zero-visual animation fires animationstart the instant the
       :-webkit-autofill pseudo-class applies, giving React a hook
       (onAnimationStart + isAutofillPing) to detect the prefill. */
    animation-name: ${autofillPing};
    animation-duration: 0.001s;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  background-color: ${colors.background};
  border-radius: 4px;
  min-height: 120px;
  outline: none;
  border: none;
  padding: 5px 10px;
  margin-top: 5px;
  font-size: 12px;
  position: relative;
  resize: none;

  ${({ variant }) => variant === "secondary" && secondaryStyles}
  ${({ isValid }) => isValid === true && successStyles}
  ${({ isValid }) => isValid === false && errorStyles}

  /* ISP-9999 #511: no local :focus purple border line — the app-wide
     :focus-visible double ring (src/styles/focusRing.css) already covers
     keyboard focus for every input/textarea. */

  &:disabled {
    background-color: ${colors.lightGrey};
    cursor: not-allowed;
  }
`;

// Password show/hide eye toggle (Gordon's universal password-eye law).
// A real <button type="button"> — keyboard-operable natively (Enter/Space),
// never submits the form, screen-reader labelled — with a >=32px hit target
// per WCAG. The icon is centered inside the target; the field's paddingRight
// (45px) reserves clear space so the button never overlaps the value.
const PasswordToggle = styled.button`
  position: absolute;
  right: 8px;
  bottom: 2px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.shadow.focus};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const PasswordIcon = styled.img`
  width: 20px;
  height: 20px;
  display: block;
  opacity: 0.8;
  pointer-events: none;
`;

const Error = styled(TextView)`
  color: ${colors.error};
  position: absolute;
`;

export default function Input({
  label,
  error,
  type = "input",
  inputStyle,
  labelStyle,
  containerStyle,
  disabled,
  variant,
  isValid,
  forceInvalid,
  value,
  isSearch,
  customBgColor,
  placeholder = isSearch ? "Search" : "",
  showClearIcon,
  onClear,
  trimPaste,
  onPaste: consumerOnPaste,
  ...rest
}) {
  const [showPassword, setShowPassword] = useState(false);
  const isInputValid = value ? isValid : null;
  // Strip accidental leading/trailing whitespace (and newlines/tabs) from a
  // paste into accuracy-critical fields — a pasted trailing space is invisible
  // and silently breaks login / unlock / verification. Default ON for password
  // and single-line non-search inputs; opt out with trimPaste={false}. Internal
  // characters are preserved (a password may legitimately contain a space).
  const shouldTrimPaste =
    trimPaste ?? (type === "password" || (type !== "textarea" && !isSearch));
  const handleTrimPaste = (e) => {
    const pasted =
      (e.clipboardData || window.clipboardData)?.getData("text") ?? "";
    const cleaned = pasted.replace(/[\r\n\t]/g, "").replace(/^\s+|\s+$/g, "");
    if (cleaned !== pasted) {
      e.preventDefault();
      const el = e.target;
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? el.value.length;
      const next = el.value.slice(0, start) + cleaned + el.value.slice(end);
      const setValue = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        "value"
      )?.set;
      if (setValue) {
        setValue.call(el, next);
        el.dispatchEvent(new Event("input", { bubbles: true }));
        const caret = start + cleaned.length;
        el.setSelectionRange(caret, caret);
      }
    }
    consumerOnPaste?.(e);
  };
  const pasteHandler = shouldTrimPaste ? handleTrimPaste : consumerOnPaste;
  // aria-invalid reflects either an explicit error or the forced (empty
  // required-field) error state, so screen readers announce the field as bad.
  const ariaInvalid =
    forceInvalid || isInputValid === false ? true : undefined;

  // H9 (a11y-i18n-mobileweb): a string `label` was rendered as a bare visible
  // <TextView> with no htmlFor/id — decorative only, giving the input no
  // programmatic accessible name. Derive an aria-label from it so screen
  // readers announce the field. A caller-supplied aria-label (via {...rest})
  // still wins because it is spread AFTER this prop on every input element.
  const derivedAriaLabel =
    typeof label === "string" && label.trim() ? label : undefined;

  return (
    <InputWrapper style={containerStyle}>
      {label ? <TextView style={labelStyle}>{label}</TextView> : null}
      {type === "textarea" ? (
        <TextArea
          className={`${disabled && "disabled"}}`}
          value={value}
          disabled={disabled}
          style={inputStyle}
          variant={variant}
          isValid={isInputValid}
          forceInvalid={forceInvalid}
          aria-label={derivedAriaLabel}
          aria-invalid={ariaInvalid}
          placeholder={placeholder}
          {...rest}
          onPaste={pasteHandler}
        />
      ) : type === "password" ? (
        <>
          <CustomInput
            value={value}
            className={`${disabled && "disabled"}}`}
            disabled={disabled}
            variant={variant}
            style={{ paddingRight: 45, ...inputStyle }}
            type={passwordInputType(showPassword)}
            isValid={isInputValid}
            forceInvalid={forceInvalid}
            aria-label={derivedAriaLabel}
            aria-invalid={ariaInvalid}
            placeholder={placeholder}
            customBgColor={customBgColor}
            // T22.620: revealing flips type to text, where iOS Safari would
            // capitalise/autocorrect the first letter. Same attrs in both states.
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            {...rest}
            onPaste={pasteHandler}
          />
          <PasswordToggle
            type="button"
            aria-label={passwordToggleLabel(showPassword)}
            aria-pressed={showPassword}
            title={passwordToggleLabel(showPassword)}
            disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            onClick={() => setShowPassword(!showPassword)}
          >
            <PasswordIcon
              alt=""
              aria-hidden="true"
              src={showPassword ? ASSETS.peye : ASSETS.peye_off}
            />
          </PasswordToggle>
        </>
      ) : (
        <>
          {isSearch && (
            <SearchIcon
              style={{
                position: "absolute",
                zIndex: 100,
                marginTop: 12,
                marginLeft: 8,
                fontSize: 22,
                color: colors.lightGrey,
              }}
            />
          )}
          <CustomInput
            value={value}
            className={`${disabled && "disabled"}}`}
            disabled={disabled}
            variant={variant}
            style={{
              paddingLeft: isSearch ? 32 : 10,
              paddingRight: showClearIcon ? 32 : 10,
              ...inputStyle,
            }}
            isValid={isInputValid}
            forceInvalid={forceInvalid}
            aria-label={derivedAriaLabel}
            aria-invalid={ariaInvalid}
            placeholder={placeholder}
            // Re-apply `type` after destructuring — without this, type="email"/
            // "tel"/etc. is silently dropped and the browser falls back to text
            // (no mobile email keyboard). Password/textarea branches set type
            // themselves; default branch must forward the consumer's type.
            type={type === "input" ? "text" : type}
            {...rest}
            onPaste={pasteHandler}
          />
          {showClearIcon && (
            <Close
              style={{
                position: "absolute",
                right: 5,
                top: 12,
                color: colors.lightGrey,
                fontSize: 22,
                cursor: "pointer",
              }}
              onClick={onClear}
            />
          )}
        </>
      )}

      {error ? <Error variant="tag">*{error}</Error> : null}
    </InputWrapper>
  );
}
